import { useEffect, useRef, useState } from 'react';
import { Button } from './Button';

/**
 * Checklist de selección múltiple (igual comportamiento que el filtro de
 * una tabla dinámica de Excel): buscador, "Seleccionar todo", y
 * Aplicar/Cancelar explícitos (no se aplica nada hasta que el usuario
 * confirma). `selected = []` se interpreta como "- all -" (sin filtro).
 *
 * Extraído de "Tabla Resumen" para reutilizarse en cualquier pantalla que
 * necesite el mismo patrón de filtro (ver "Comparar Clientes").
 */
export function MultiSelectChecklist({
  label,
  options,
  selected,
  onApply,
  renderLabel,
  allLabel = '(Todas)',
  selectedSuffix = 'seleccionadas',
}: {
  label: string;
  options: string[];
  selected: string[];
  onApply: (next: string[]) => void;
  /** Texto a mostrar por opción (por defecto la misma opción). Útil cuando el valor interno no es el texto legible (ej. "julio" -> "Julio"). */
  renderLabel?: (option: string) => string;
  /** Texto cuando no hay filtro (selected=[]). Por defecto "(Todas)"; usar "(Todos)" para sustantivos masculinos como "Mes". */
  allLabel?: string;
  /** Palabra usada en "N seleccionadas/seleccionados" - ajustar género según el sustantivo del label. */
  selectedSuffix?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string[]>(selected);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const display = renderLabel ?? ((o: string) => o);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const openPanel = () => {
    setPending(selected.length === 0 ? [...options] : selected);
    setSearch('');
    setOpen(true);
  };
  const filtered = options.filter((o) => display(o).toLowerCase().includes(search.toLowerCase()));
  const allChecked = pending.length === options.length && options.length > 0;
  const toggleAll = () => setPending(allChecked ? [] : [...options]);
  const toggleOne = (o: string) => setPending((prev) => (prev.includes(o) ? prev.filter((x) => x !== o) : [...prev, o]));

  const summaryLabel = selected.length === 0 ? allLabel : selected.length === 1 ? display(selected[0]) : `${selected.length} ${selectedSuffix}`;

  return (
    <div className="relative" ref={ref}>
      <span className="mb-1.5 block text-sm font-medium text-ink-700">{label}</span>
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openPanel())}
        className="h-[50px] w-full rounded-xl border border-ink-200 bg-surface px-4 text-left text-[15px] text-ink-800 outline-none focus:ring-2 focus:ring-brand-200"
      >
        {summaryLabel}
      </button>

      {open && (
        <div className="absolute z-20 mt-2 w-full min-w-[260px] rounded-xl border border-ink-100 bg-surface shadow-lg">
          <div className="border-b border-ink-100 p-2">
            <input
              autoFocus
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-full rounded-lg border border-ink-200 bg-surface px-3 text-sm text-ink-800 outline-none focus:ring-2 focus:ring-brand-200"
            />
          </div>
          <div className="max-h-[260px] overflow-y-auto p-2">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50">
              <input type="checkbox" checked={allChecked} onChange={toggleAll} className="h-4 w-4 rounded border-ink-300" />
              Seleccionar todo
            </label>
            <div className="my-1 border-t border-ink-100" />
            {filtered.length === 0 ? (
              <p className="px-2 py-2 text-sm text-ink-400">Sin resultados.</p>
            ) : (
              filtered.map((o) => (
                <label key={o} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-ink-700 hover:bg-ink-50">
                  <input type="checkbox" checked={pending.includes(o)} onChange={() => toggleOne(o)} className="h-4 w-4 rounded border-ink-300" />
                  {display(o)}
                </label>
              ))
            )}
          </div>
          <div className="flex gap-2 border-t border-ink-100 p-2">
            <Button variant="outline" size="md" fullWidth onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button
              size="md"
              fullWidth
              onClick={() => {
                onApply(pending.length === options.length ? [] : pending);
                setOpen(false);
              }}
            >
              Aplicar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
